<template>
  <span v-if="promotable_document || outcome_state" class="inline-flex items-center">
    <!-- Already in the store's knowledge: an icon that says so, not a button that invites a second click. -->
    <i v-if="outcome_state === 'kept'" class="ri-building-2-fill ri-150 text-text-link" :title="outcome_title" />
    <i v-else-if="outcome_state === 'promoted'" class="ri-building-2-fill ri-150 text-text-placeholder" :title="outcome_title" />
    <i v-else-if="outcome_state === 'pending'" class="ri-time-fill ri-150 text-text-placeholder" :title="outcome_title" />
    <i v-else-if="outcome_state === 'failed'" class="ri-error-warning-fill ri-150 text-danger" :title="outcome_title" />
    <div v-else-if="promoting" class="fusion-loader loader-md" :title="$t('messaging.promoteAttachment.keeping', { fileName })" />
    <i v-else class="ri-building-2-line ri-150 text-text-placeholder hover:text-text-link cursor-pointer"
       :title="offer_title" @click.stop="promote" />
  </span>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';

/**
 * Offers to promote one chat attachment into the store's knowledge, from the attachment's own card.
 *
 * A file dropped into a conversation is ingested under that thread with a 24h TTL: it answers questions
 * here and then disappears. That is the right default for the screenshot someone pasted, and the wrong
 * outcome for the runbook they happened to share. This is the one-click path from the first to the second.
 *
 * It sits next to the download icon deliberately. The action belongs on the object it applies to, and it
 * stays reachable for as long as the message is in the conversation — an offer that only appears right
 * after the upload is gone by the time anyone realises the file is worth keeping.
 *
 * The document id comes from the shared thread-document list in the store, not from the message: the
 * message carries a blob URL and a file name, and the promotion needs the DocumentRecord the ingestion
 * pipeline created. Reading it back is also what tells us a file has already been promoted, which the
 * message never could.
 */
export default {
  name: 'PromoteAttachmentButton',
  props: {
    threadId: {
      type: String,
      required: true,
    },
    fileName: {
      type: String,
      required: true,
    },
  },
  emits: [],
  mounted() {
    this.loadThreadDocuments();
    window.event_bus.$on('ATTACHMENT-UPLOADED', this.onAttachmentUploaded);
  },
  beforeUnmount() {
    window.event_bus.$off('ATTACHMENT-UPLOADED', this.onAttachmentUploaded);
  },
  data() {
    return {
      promoting: false,
      // Set once this card's file has been acted on, so the icon stops being an offer: 'kept' (retrievable
      // now), 'pending' (waiting for an approver), 'failed'. Local because it describes THIS click.
      local_outcome: '',
    };
  },
  computed: {
    ...mapStores(useMainStore),

    thread_documents() {
      return this.mainStore.thread_documents;
    },

    current_cif() {
      return this.mainStore.current_cif;
    },

    /** This card's document, matched on file name — newest first, since a name can repeat in a thread. */
    matching_document() {
      const mine = (this.thread_documents || []).filter(doc =>
        doc && doc.id && doc.fileName === this.fileName);
      return mine.length ? mine[0] : null;
    },

    /** Present only while promoting is still available: an already-promoted file is not offered again. */
    promotable_document() {
      const doc = this.matching_document;
      return doc && !doc.promotedToDealer ? doc : null;
    },

    /**
     * What to render.
     * <p>
     * A promotion recorded on the server outranks nothing local: the thread listing says THAT a file was
     * promoted, not whether the promotion has been approved, so on its own it cannot distinguish "the store
     * can query this" from "an admin still has to approve it". When this session did the promoting we know
     * which (the API returned the status), so the local answer is preferred; otherwise the neutral
     * 'promoted' state says only what is actually known. Claiming "anyone can ask about it" for a document
     * still sitting in a review queue is a lie the user only discovers by asking Rey and getting nothing.
     */
    outcome_state() {
      if (this.local_outcome) return this.local_outcome;
      if (this.matching_document?.promotedToDealer) return 'promoted';
      return '';
    },

    outcome_title() {
      switch (this.outcome_state) {
        case 'kept':
          return this.$t('messaging.promoteAttachment.kept', { fileName: this.fileName });
        case 'promoted':
          return this.$t('messaging.promoteAttachment.promoted', { fileName: this.fileName });
        case 'pending':
          return this.$t('messaging.promoteAttachment.pending', { fileName: this.fileName });
        case 'failed':
          return this.$t('messaging.promoteAttachment.failed', { fileName: this.fileName });
        default:
          return '';
      }
    },

    offer_title() {
      return this.$t('messaging.promoteAttachment.offer', { fileName: this.fileName });
    },
  },
  methods: {
    // Store actions are always reached through a local wrapper, per the project convention.
    loadThreadDocuments(force = false) {
      // Silent on failure: this is an optional offer, and an error for something the user never asked for
      // would be worse than the icon not appearing.
      this.mainStore.LoadThreadDocuments(this.threadId, force).catch(() => {});
    },

    promoteThreadDocument(documentId) {
      return this.mainStore.PromoteThreadDocument({
        documentId,
        threadId: this.threadId,
        dealerId: this.current_cif,
      });
    },

    onAttachmentUploaded() {
      this.loadThreadDocuments(true);
    },

    promote() {
      const doc = this.promotable_document;
      if (!doc || this.promoting) return;

      // Without a store in context there is nowhere to promote TO, and guessing one would put a user's
      // file into a dealership they may not even be looking at.
      if (!this.current_cif) {
        this.local_outcome = 'failed';
        return;
      }

      const self = this;
      this.promoting = true;
      this.promoteThreadDocument(doc.id).then(record => {
        self.promoting = false;
        self.local_outcome = self.outcomeStateFor(record);
        // Refresh so every other card in the thread sees the new state too, and so a reload keeps it.
        self.loadThreadDocuments(true);
      }, () => {
        self.promoting = false;
        self.local_outcome = 'failed';
      });
    },

    /**
     * A promoted document is not necessarily usable yet: the store corpus is review-gated, so an ordinary
     * user's promotion waits for an admin. Showing "anyone can ask about it" in that case would be a claim
     * the user only discovers is false by asking.
     */
    outcomeStateFor(record) {
      const status = this.statusNameOf(record);
      if (status === 'PendingReview') return 'pending';
      if (status === 'Active' || status === 'Extracted') return 'kept';
      return 'pending';
    },

    /**
     * The status arrives as a name or as an enum ordinal depending on the writer, so both are handled —
     * treating an ordinal as unknown would mislabel every promotion.
     */
    statusNameOf(record) {
      const raw = record && record.status !== undefined ? record.status : null;
      if (typeof raw === 'string') return raw;
      if (typeof raw === 'number') {
        // Mirrors DocumentStatus in Abstractions; ordinal-indexed, so append-only.
        const names = ['Received', 'Assessed', 'Extracted', 'PendingReview', 'Active', 'Superseded', 'Expired', 'Archived'];
        return raw >= 0 && raw < names.length ? names[raw] : '';
      }
      return '';
    },
  },
};
</script>
